import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { headerForHazard, headerForKPI } from "./AgroAnalyticsUtils";
import { fixedFormatter } from "../util/tableUtils";
import { hazardGaranzias } from "./AgroBivariateLegend";
import i18n from "i18next";
import { Grid } from "@material-ui/core";
import {
  mapHazardCodeToHazardProperty,
  riskLevelThresholdsAndLegends,
  getSimplifiedFromAnia,
} from "../map/BivariateLogic";

import { percent100twoDecimalsFormatter } from "../util/tableUtils";

import {
  getAggregatedData,
  getAggregatedDataWrapper,
  onFilterModelChange,
  renderGrid,
} from "./AgroAnalyticsUtils";

const getRiskLevelsTableColumns = (hazardSelect, KPISelect) => [
  {
    field: "level",
    headerName: headerForHazard(hazardSelect),
    width: 450,
  },
  {
    field: "kpiValue",
    headerName: headerForKPI(KPISelect),
    width: 400,
    ...fixedFormatter,
  },
  {
    field: "kpiValuePercents",
    headerName: "%", // name of the KPI will be here!
    width: 100,
    ...percent100twoDecimalsFormatter,
  },
];

export const AgroRiskLevelGrid = ({ filterModel, hazardSelect, KPISelect, wantedReferenceDateProp }) => {
  const { t } = useTranslation();
  const subIntervalsJson = riskLevelThresholdsAndLegends(t)[hazardSelect].riskLevelValues;
  const [riskLevelsTableState, setRiskLevelsTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const riskLevelsCancelTokenRef = useRef(null);
  const [riskLevelsTableData, setRiskLevelsTableData] = useState([]);
  const [isComputing, setIsComputing] = useState(false);

  useEffect(() => {
    const subIntervalsJsonInternal = riskLevelThresholdsAndLegends(t)[hazardSelect].riskLevelValues;
    const getRiskLevelsData = async () => {
      const cysHazardSelect = mapHazardCodeToHazardProperty[hazardSelect];
      setIsComputing(true);
      const riskLevelsData = await getAggregatedData(
        setRiskLevelsTableState,
        setRiskLevelsTableData,
        // `aggrByRisk~${hazardSelect}~${cysHazardSelect}~${KPISelect}~${lowValue}~${highValue}~${subIntervalsAsString}`,
        {
          aggr: "Risk",
          hazardSelect: getSimplifiedFromAnia(hazardSelect),
          cysHazardName: cysHazardSelect,
          KPISelect,
          subIntervals: subIntervalsJsonInternal,
        },
        filterModel,
        wantedReferenceDateProp,
        riskLevelsCancelTokenRef,
      );
      setIsComputing(false);
      return riskLevelsData;
    };
    const getRiskLevelsDataWrapper = async () => {
      getAggregatedDataWrapper(riskLevelsCancelTokenRef, getRiskLevelsData);
    };

    onFilterModelChange(filterModel, getRiskLevelsDataWrapper);
  }, [filterModel, hazardSelect, KPISelect, wantedReferenceDateProp, t]);

  const addTitlesForRows = (riskLevelsTableData) => {
    // This function converts typical input to typical output, see example below. The text is obtained from corresponding legends.

    // eslint-disable-next-line no-unused-vars
    const typicalInput = [
      { id: 1, kpiValue: 0, kpiValuePercents: 0, level: "climateAnalytics.0" },
      { id: 2, kpiValue: 0, kpiValuePercents: 0, level: "climateAnalytics.1" },
      { id: 3, kpiValue: 0, kpiValuePercents: 0, level: "climateAnalytics.2" },
      { id: 4, kpiValue: 47, kpiValuePercents: 0.8245614035087719, level: "climateAnalytics.3" },
      { id: 5, kpiValue: 10, kpiValuePercents: 0.17543859649122806, level: "climateAnalytics.4" },
      { id: 6, kpiValue: 0, kpiValuePercents: 0, level: "climateAnalytics.5" },
      { id: 7, kpiValue: 0, kpiValuePercents: 0, level: "climateAnalytics.6" },
      { id: 8, kpiValue: 0, kpiValuePercents: 0, level: "climateAnalytics.7" },
      { id: 9, kpiValue: 0, kpiValuePercents: 0, level: "climateAnalytics.8" },
      { id: 10, kpiValue: 57, kpiValuePercents: 1, level: "climateAnalytics.9" },
    ];
    // eslint-disable-next-line no-unused-vars
    const typicalOutput = [
      { id: 1, kpiValue: 0, kpiValuePercents: 0, level: "Nessun dato : (ph < 0)" },
      { id: 2, kpiValue: 0, kpiValuePercents: 0, level: "Basso : (0 ≤ ph < 0.005)" },
      { id: 3, kpiValue: 0, kpiValuePercents: 0, level: "Moderato : (0.005 ≤ ph < 0.0125)" },
      {
        id: 4,
        kpiValue: 47,
        kpiValuePercents: 0.8245614035087719,
        level: "Significativo : (0.0125 ≤ ph < 0.02)",
      },
      { id: 5, kpiValue: 10, kpiValuePercents: 0.17543859649122806, level: "Alto : (ph ≥ 0.02)" },
      { id: 6, kpiValue: 0, kpiValuePercents: 0, level: "Superiore al limite superiore" },
      { id: 7, kpiValue: 0, kpiValuePercents: 0, level: "Nessuna misurazione" },
      {
        id: 8,
        kpiValue: 0,
        kpiValuePercents: 0,
        level: "Obsoleto comune, assente nei dati meteorologici del 2023",
      },
      { id: 9, kpiValue: 0, kpiValuePercents: 0, level: "Non è noto l'indirizzo di queste proprietà" },
      { id: 10, kpiValue: 57, kpiValuePercents: 1, level: "Somma totale" },
    ];

    let riskLevelsTableDataTranslated = riskLevelsTableData;
    if (!isComputing && riskLevelsTableDataTranslated.length > 0) {
      const nRows = riskLevelsTableData.length;

      // Keep this sync with back-end code in  createKPITable.
      if (subIntervalsJson.strictStepValues) {
        // if  subIntervalsJson are "strictStepValues":[5,7] then two sums are for 5,7, we use 2+4 bins.

        // nRows == subIntervalsJson.strictStepValues.length + 4

        for (let row = 0; row < nRows - 4; row++) {
          riskLevelsTableDataTranslated[row].level =
            riskLevelThresholdsAndLegends(t)[hazardSelect].riskNames[row];
        }
      } else if (subIntervalsJson.intervalBoundaries) {
        // if  subIntervalsJson are "intervalBoundaries":[5,7] then three sums are for -Inf-5, 5-7, 7-Inf, we use 2+1+4 bins.

        // nRows !== subIntervalsJson.intervalBoundaries.length + 5) {

        for (let row = 0; row < nRows - 5; row++) {
          riskLevelsTableDataTranslated[row].level =
            riskLevelThresholdsAndLegends(t)[hazardSelect].riskNames[row];
        }
        riskLevelsTableDataTranslated[nRows - 5].level = i18n.t(
          "riskLevelAnalyticsTableEntries.higherThanUpperBound",
        );
      } else {
        console.error("Error in riskLevelsTableDataTranslated", subIntervalsJson);
      }
      // Then 4 extra entries were added for corner cases and the sum.

      riskLevelsTableDataTranslated[nRows - 4].level = i18n.t(
        "riskLevelAnalyticsTableEntries.noMeasurements",
      );
      riskLevelsTableDataTranslated[nRows - 3].level = i18n.t("riskLevelAnalyticsTableEntries.obsolete");
      riskLevelsTableDataTranslated[nRows - 2].level = i18n.t("riskLevelAnalyticsTableEntries.noAddress");
      riskLevelsTableDataTranslated[nRows - 1].level = i18n.t("riskLevelAnalyticsTableEntries.total");
    }

    return riskLevelsTableDataTranslated;
  };

  let riskLevelsTableDataTranslated = addTitlesForRows(riskLevelsTableData);
  const explainRiskLevels = (KPISelect, hazardSelect) => {
    return (
      <Grid item xs={12} sm={12}>
        <ul>
          <li>
            {i18n.t("analyticsLevels.thisKPIIs")} <b>{headerForKPI(KPISelect)}</b>{" "}
            {i18n.t("analyticsLevels.inThe")} {i18n.t("climateAnalytics.referenceDate")}{" "}
            <b>{wantedReferenceDateProp}</b>.
          </li>
          {KPISelect === "none" ? (
            <li>{i18n.t("analyticsLevels.value1")}</li>
          ) : (
            <li>{i18n.t("analyticsLevels.matchOnly")} </li>
          )}
          <li>
            {i18n.t("analyticsLevels.hazardConsidered")} <b>{headerForHazard(hazardSelect)}</b>
            {i18n.t("analyticsLevels.hazardConsidered2")}{" "}
            <a href="/#/riskassessment" target="other">
              (Link)
            </a>
            . {i18n.t("analyticsLevels.centre")}
          </li>
          <li>
            {i18n.t("analyticsLevels.coveragesForHazard")} <b>{headerForHazard(hazardSelect)}</b> :{" "}
            {hazardGaranzias(hazardSelect, KPISelect)}
          </li>
        </ul>
      </Grid>
    );
  };

  const riskLevelsTableColumns = getRiskLevelsTableColumns(hazardSelect, KPISelect);
  return (
    <Grid container>
      {explainRiskLevels(KPISelect, hazardSelect)}
      {/* The table has small number of rows, therefore pagination is turned off */}
      {renderGrid(
        riskLevelsTableColumns,
        riskLevelsTableDataTranslated,
        riskLevelsTableState,
        t,
        "Livelli_Rischio_" + KPISelect + "_" + hazardSelect,
        {
          pagination: false,
        },
      )}
    </Grid>
  );
};
